/**
 * Appstle Subscriptions widget — pure helper functions.
 *
 * These helpers parse the prices, discount labels and plan names that
 * Appstle's widget renders into nested DOM. They are kept separate
 * from the main entry (`appstle-tweak.js`) so the file can stay under
 * the 300-line lint limit and so the helpers can be unit-tested in
 * isolation.
 *
 * Exposed on `window.GG_AppstleHelpers` so the main entry — loaded
 * after this file via two `<script defer>` tags — can pick them up
 * synchronously.
 */
(function () {
  'use strict';

  function extractPerDeliveryPrice(opt) {
    // Appstle wraps prices in <span class="transcy-money">. Some currency
    // converters strip nested spans inside <option>, so we fall back to
    // a regex over textContent when the inner span is unavailable.
    if (opt && typeof opt.querySelector === 'function') {
      var priceSpan = opt.querySelector('.transcy-money, [class*="money"]');
      if (priceSpan && priceSpan.textContent) {
        var inner = priceSpan.textContent.trim();
        if (/\$[\d,]+/.test(inner)) return inner;
      }
    }
    var text = opt && opt.textContent ? opt.textContent : String(opt || '');
    var prices = text.match(/\$[\d,]+(\.\d+)?/g);
    if (!prices) return '';
    // The option text format is "<plan name> (<price>/delivery)" — the
    // last $ price is the per-delivery rate.
    return prices[prices.length - 1];
  }

  function extractDiscountLabel(text) {
    var match = text.match(/(?:SAVE\s*)?[−\-]?\d+%/i);
    return match ? match[0].toUpperCase().replace(/^[−\-]/, '−') : '';
  }

  function extractPlanName(text) {
    return text
      .replace(/\(.*?\)/g, '')
      .replace(/［.*?］/g, '')
      .replace(/\[.*?\]/g, '')
      .replace(/\$[\d,]+(\.\d+)?(\s*\/\s*\w+)?/g, '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Compute the per-delivery price by discounting the monthly base price
   * by the percentage in the badge label. Used because Appstle's <option>
   * text is unreliable (currency converters / async render mutate it).
   * Returns the original currency prefix preserved (e.g. "$", "S$").
   */
  function calcDiscountedPrice(basePrice, discountLabel) {
    if (!basePrice || !discountLabel) return '';
    var baseMatch = basePrice.trim().match(/^([^\d.,\s-]+)?\s*([\d,]+(?:\.\d+)?)/);
    var discountMatch = discountLabel.match(/(\d+)/);
    if (!baseMatch || !discountMatch) return '';
    var prefix = (baseMatch[1] || '$').trim();
    var baseNum = parseFloat(baseMatch[2].replace(/,/g, ''));
    var discountPct = parseInt(discountMatch[1], 10);
    if (isNaN(baseNum) || isNaN(discountPct)) return '';
    var discounted = baseNum * (1 - discountPct / 100);
    var formatted = discounted.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return prefix + formatted;
  }

  /* Astro mock parity: helper text shown under each plan name. */
  var PLAN_HELPER = {
    Monthly: 'Flexible — cancel anytime',
    'Monthly Plan': 'Flexible — cancel anytime',
    '6 Month Plan': 'Save 10% — billed every 6 months',
    'Annual Plan': 'Best value — billed yearly',
  };

  /* Normalize plan names from Appstle to match the Astro mock copy. */
  function normalizePlanName(name) {
    if (!name) return name;
    var n = name.trim();
    if (/^6\s*months?\s*plan/i.test(n)) return '6 Month Plan';
    if (/^annual\s*plan/i.test(n)) return 'Annual Plan';
    if (/^monthly(\s*plan)?$/i.test(n)) return 'Monthly Plan';
    return n;
  }

  function injectHelperText(card, planName) {
    if (!card) return;
    var helperCopy = PLAN_HELPER[planName];
    if (!helperCopy) return;
    var existing = card.querySelector('.gg-plan-helper');
    if (existing) {
      existing.textContent = helperCopy;
      return;
    }
    var wrapper = card.querySelector('.appstle-text-and-badge-wrapper');
    if (!wrapper) return;
    var helper = document.createElement('span');
    helper.className = 'gg-plan-helper';
    helper.textContent = helperCopy;
    wrapper.appendChild(helper);
  }

  window.GG_AppstleHelpers = {
    extractPerDeliveryPrice: extractPerDeliveryPrice,
    extractDiscountLabel: extractDiscountLabel,
    extractPlanName: extractPlanName,
    calcDiscountedPrice: calcDiscountedPrice,
    normalizePlanName: normalizePlanName,
    injectHelperText: injectHelperText,
  };
})();
